import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, animate } from 'framer-motion';

const Counter = ({ from = 0, to, suffix = '', duration = 2 }) => {
  const nodeRef = useRef();
  const isInView = useInView(nodeRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;
    const node = nodeRef.current;
    const controls = animate(from, to, {
      duration,
      ease: "easeOut",
      onUpdate(value) {
        node.textContent = Math.round(value).toLocaleString() + suffix;
      }
    });
    return () => controls.stop();
  }, [from, to, duration, suffix, isInView]);

  return <span ref={nodeRef}>{from}{suffix}</span>;
};

export const StatsCounter = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    { value: 12, suffix: ' / 24 / 52', label: 'Week Programs', sub: 'Choose your timeline' },
    { value: 100, suffix: '%', label: '1:1 Coaching', sub: 'Never templated' },
    { value: 7, suffix: '', label: 'Days of Support', sub: 'Direct messaging access' },
    { value: 10, suffix: '+', label: 'Coached People', sub: 'Across finance, sales, ops' }
  ];

  return (
    <section ref={ref} className="relative bg-panel py-24 md:py-32 px-6 overflow-hidden">
      {/* Animated Background */}
      <motion.div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-px bg-gradient-to-r from-transparent via-gold to-transparent"
        animate={{ opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-px bg-gradient-to-r from-transparent via-gold to-transparent"
        animate={{ opacity: [1, 0.3, 1] }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="font-accent text-xl md:text-2xl italic text-gold mb-4">
            The System By The Numbers
          </p>
          <h2 className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream" style={{ fontWeight: 700 }}>
            Engineered to Compound
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ stat, index, isInView }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -20;
    setMousePosition({ x, y });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 100, rotateY: -45 }}
      animate={isInView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
      transition={{ delay: index * 0.15, duration: 0.8, type: "spring", stiffness: 80 }}
      style={{ perspective: '1500px' }}
    >
      <motion.div
        ref={cardRef}
        className="bg-dark border-2 border-gold/30 p-6 md:p-8 text-center group cursor-pointer relative overflow-hidden"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
        animate={{
          rotateX: mousePosition.y * 0.3,
          rotateY: mousePosition.x * 0.3,
        }}
        whileHover={{
          borderColor: 'rgba(198, 160, 74, 0.9)',
          boxShadow: '0 25px 60px rgba(198, 160, 74, 0.3)'
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        <div style={{ transform: 'translateZ(40px)' }}>
          <div className="font-hero text-5xl md:text-7xl lg:text-8xl text-gold mb-3" style={{ fontWeight: 700 }}>
            <Counter to={stat.value} suffix={stat.suffix} />
          </div>
          <p className="font-display text-base md:text-lg uppercase tracking-wider text-cream mb-2">
            {stat.label}
          </p>
          <p className="font-body text-xs md:text-sm text-gold-soft font-light">
            {stat.sub}
          </p>
        </div>

        {/* Hover glow */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(198, 160, 74, 0.15), transparent 70%)'
          }}
        />

        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-gold/50 group-hover:border-gold transition-colors duration-300" />
      </motion.div>
    </motion.div>
  );
};
